import React, { useState, useEffect, useRef } from 'react';
import { LogoIcon } from './Logo';
import { useLanguage } from './LanguageContext';
import {
  getQuestionsByMode,
  getTestIdByMode,
  createTrainingSession,
  completeSession,
  saveAnswer,
  reportQuestion,
  saveReview,
  supabase,
} from './supabaseClient';

// ══════════════════════════════════════════════════════════
//  بانر رد الأدمن على ملاحظة الطالب
// ══════════════════════════════════════════════════════════
function AdminReplyBanner({ questionId, userId }) {
  const [reply, setReply] = useState(null);

  useEffect(() => {
    if (!questionId || !userId) return;
    supabase
      .from('question_reports')
      .select('admin_reply')
      .eq('question_id', questionId)
      .not('admin_reply', 'is', null)
      .order('replied_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => { if (data?.admin_reply) setReply(data.admin_reply); });
  }, [questionId, userId]);

  if (!reply) return null;

  return (
    <div style={{
      marginBottom: 10,
      padding: '12px 14px',
      background: 'rgba(16,185,129,0.08)',
      border: '1px solid rgba(16,185,129,0.3)',
      borderRadius: 12,
      direction: 'rtl',
    }}>
      <div style={{ color: '#10b981', fontSize: 12, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 16 }}>📩</span> رد الأدمن على ملاحظتك:
      </div>
      <div style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.7, fontFamily: 'Tajawal,sans-serif' }}>
        {reply}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  مكوّن الإبلاغ عن مشكلة في السؤال
// ══════════════════════════════════════════════════════════

function ReportButton({ questionId, userId, userPhone }) {
  const { t } = useLanguage();
  const [open,       setOpen]       = useState(false);
  const [note,       setNote]       = useState('');
  const [status,     setStatus]     = useState('idle'); // idle | loading | error | sent
  const [adminReply, setAdminReply] = useState(null);

  // جلب رد الأدمن عند الفتح
  const handleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && userId && questionId && adminReply === null) {
      const { data } = await supabase
        .from('question_reports')
        .select('admin_reply, replied_at')
        .eq('question_id', questionId)
        .not('admin_reply', 'is', null)
        .order('replied_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      setAdminReply(data?.admin_reply || '');
    }
  };

  const handleSubmit = async () => {
    if (!note.trim() || status !== 'idle') return;
    setStatus('loading');
    const { error } = await reportQuestion(questionId, userId, 'other', note, userPhone);
    if (!error) {
      setStatus('sent');
      setTimeout(() => {
        setStatus('idle');
        setOpen(false);
        setNote('');
      }, 2000);
    } else {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <div style={{ marginBottom: 10 }}>
      <button onClick={handleOpen} style={{
        width: '100%', background: 'transparent',
        border: '1px solid #1e2a42', borderRadius: open ? '10px 10px 0 0' : 10,
        padding: '8px 14px', cursor: 'pointer',
        fontFamily: 'Tajawal,sans-serif',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        transition: 'all 0.2s',
      }}>
        <span style={{ color: adminReply ? '#10b981' : 'var(--text-muted)', fontSize: 12 }}>
          {adminReply ? '📩 رد الأدمن على ملاحظتك' : `💬 ${t('study_reportProblem')}`}
        </span>
        <span style={{ color: 'var(--border)', fontSize: 11 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          background: '#0a0f1a', border: '1px solid #1e2a42',
          borderTop: 'none', borderRadius: '0 0 10px 10px',
          padding: '12px 14px',
        }}>
          {/* رد الأدمن */}
          {adminReply && (
            <div style={{
              marginBottom: 12, padding: '10px 14px',
              background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)',
              borderRadius: 10,
            }}>
              <div style={{ color: '#10b981', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                📩 رد الأدمن على ملاحظتك:
              </div>
              <div style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.6, direction: 'rtl' }}>
                {adminReply}
              </div>
            </div>
          )}
          {status === 'sent' ? (
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 6 }}>✅</div>
              <div style={{ color: '#86efac', fontSize: 13, fontWeight: 700 }}>
                {t('study_reportSentTitle')}
              </div>
            </div>
          ) : (
            <>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="اكتب ملاحظتك على السؤال..."
                maxLength={300}
                rows={3}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: '#0d1321', border: '1px solid #1e2a42',
                  borderRadius: 8, padding: '10px 12px',
                  color: 'var(--text)', fontSize: 13,
                  fontFamily: 'Tajawal,sans-serif',
                  resize: 'none', outline: 'none',
                  marginBottom: 8, direction: 'rtl',
                  lineHeight: 1.6,
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                  {note.length}/300
                </span>
                <button
                  onClick={handleSubmit}
                  disabled={!note.trim() || status === 'loading'}
                  style={{
                    padding: '8px 20px',
                    background: !note.trim() || status === 'loading'
                      ? '#1e2a42'
                      : status === 'error'
                      ? '#ef4444'
                      : 'linear-gradient(135deg,#7c6df8,#a78bfa)',
                    border: 'none', borderRadius: 8,
                    color: !note.trim() ? 'var(--text-muted)' : '#fff',
                    fontSize: 13, fontWeight: 700,
                    cursor: !note.trim() || status === 'loading' ? 'not-allowed' : 'pointer',
                    fontFamily: 'Tajawal,sans-serif',
                    transition: 'all 0.2s',
                  }}>
                  {status === 'loading' ? '⏳ جاري...' : status === 'error' ? '❌ فشل' : '📤 إرسال'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  مكوّن تقييم المنصة بعد الجلسة
// ══════════════════════════════════════════════════════════
const SPECS = {
  'ثانوي علمي': ['علوم طبيعية', 'رياضيات وحاسب'],
  'ثانوي أدبي': ['آداب وإنسانيات', 'إدارة الأعمال'],
};

function ReviewBox({ user }) {
  const { t } = useLanguage();
  const [open,      setOpen]      = useState(false);
  const [stars,     setStars]     = useState(0);
  const [hover,     setHover]     = useState(0);
  const [comment,   setComment]   = useState('');
  const [name,      setName]      = useState('');
  const [stage,     setStage]     = useState('');
  const [spec,      setSpec]      = useState('');
  const [status,    setStatus]    = useState('idle'); // idle | loading | done

  const handleSend = async () => {
    if (!stars || status !== 'idle') return;
    setStatus('loading');
    const displayName = name.trim() || (user?.phone ? user.phone.slice(-4) : 'مشترك');
    const fullStage = stage ? (spec ? `${stage} - ${spec}` : stage) : '';
    const { error } = await saveReview({ displayName, stars, comment, stage: fullStage });
    if (!error) {
      setStatus('done');
    } else {
      setStatus('idle');
    }
  };

  if (status === 'done') return (
    <div style={{
      background: 'rgba(34,197,94,0.07)', border: '1px solid rgba(34,197,94,0.2)',
      borderRadius: 14, padding: '14px', marginBottom: 12, textAlign: 'center',
      fontFamily: 'Tajawal,sans-serif',
    }}>
      <div style={{ fontSize: 28, marginBottom: 6 }}>🙏</div>
      <div style={{ color: '#86efac', fontWeight: 700, fontSize: 14 }}>{t('study_reviewDone')}</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>{t('study_reviewHelps')}</div>
    </div>
  );

  return (
    <div style={{ marginBottom: 12 }}>
      <button onClick={() => setOpen(p => !p)} style={{
        width: '100%',
        background: open ? 'rgba(251,191,36,0.1)' : 'rgba(251,191,36,0.06)',
        border: `1px solid ${open ? 'rgba(251,191,36,0.4)' : 'rgba(251,191,36,0.2)'}`,
        borderRadius: open ? '14px 14px 0 0' : 14, padding: '12px 16px',
        cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        transition: 'all 0.2s',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>⭐</span>
          <span style={{ color: '#fbbf24', fontSize: 13, fontWeight: 700 }}>{t('study_writeReview')}</span>
        </span>
        <span style={{ color: '#fbbf24', fontSize: 11 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          background: '#0a0f1a', border: '1px solid rgba(251,191,36,0.2)',
          borderTop: 'none', borderRadius: '0 0 14px 14px', padding: '16px 14px',
          fontFamily: 'Tajawal,sans-serif',
        }}>
          {/* النجوم */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
            {[1,2,3,4,5].map(n => (
              <button key={n}
                onClick={() => setStars(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 34, padding: 0,
                  filter: (hover || stars) >= n ? 'none' : 'grayscale(1) opacity(0.3)',
                  transform: (hover || stars) >= n ? 'scale(1.2)' : 'scale(1)',
                  transition: 'all .15s',
                }}>⭐</button>
            ))}
          </div>

          {stars > 0 && (
            <p style={{
              color: ['','#ef4444','#f97316','#f59e0b','#22c55e','#a78bfa'][stars],
              fontWeight: 700, fontSize: 14, textAlign: 'center', margin: '0 0 12px',
            }}>
              {['', t('study_star1'), t('study_star2'), t('study_star3'), t('study_star4'), t('study_star5')][stars]}
            </p>
          )}

          {/* الاسم */}
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('study_namePlaceholder')}
            maxLength={40}
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'var(--bg-card)', border: '1px solid #1e2a42',
              borderRadius: 10, padding: '9px 12px',
              color: 'var(--text)', fontSize: 13,
              fontFamily: 'Tajawal,sans-serif',
              outline: 'none', marginBottom: 8, direction: 'rtl',
            }}
          />

          {/* المرحلة الدراسية */}
          <select
            value={stage}
            onChange={e => { setStage(e.target.value); setSpec(''); }}
            style={{
              width: '100%', boxSizing: 'border-box', marginBottom: 8,
              background: 'var(--bg-card)', border: '1px solid #1e2a42',
              borderRadius: 10, padding: '9px 12px',
              color: stage ? 'var(--text)' : 'var(--text-secondary)',
              fontSize: 13, fontFamily: 'Tajawal,sans-serif',
              outline: 'none', direction: 'rtl', cursor: 'pointer',
            }}
          >
            <option value="">{t('study_stagePlaceholder')}</option>
            <option value="ثانوي علمي">ثانوي علمي</option>
            <option value="ثانوي أدبي">ثانوي أدبي</option>
            <option value="طالب جامعي">طالب جامعي</option>
            <option value="أخرى">أخرى</option>
          </select>

          {/* التخصص */}
          {SPECS[stage] && (
            <select
              value={spec}
              onChange={e => setSpec(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box', marginBottom: 8,
                background: 'var(--bg-card)', border: '1px solid #fbbf24',
                borderRadius: 10, padding: '9px 12px',
                color: spec ? 'var(--text)' : 'var(--text-secondary)',
                fontSize: 13, fontFamily: 'Tajawal,sans-serif',
                outline: 'none', direction: 'rtl', cursor: 'pointer',
              }}
            >
              <option value="">التخصص</option>
              {SPECS[stage].map(sp => (
                <option key={sp} value={sp}>{sp}</option>
              ))}
            </select>
          )}

          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder={t('study_commentPlaceholder')}
            rows={2}
            maxLength={300}
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'var(--bg-card)', border: '1px solid #1e2a42',
              borderRadius: 10, padding: '9px 12px',
              color: 'var(--text)', fontSize: 13,
              fontFamily: 'Tajawal,sans-serif',
              resize: 'none', outline: 'none',
              marginBottom: 10, direction: 'rtl',
            }}
          />

          <button
            onClick={handleSend}
            disabled={!stars || status === 'loading'}
            style={{
              width: '100%', padding: '11px',
              background: !stars ? 'var(--border)' : status === 'loading' ? 'var(--border)'
                : 'linear-gradient(135deg,#f59e0b,#fbbf24)',
              border: 'none', borderRadius: 10,
              color: !stars ? 'var(--text-muted)' : '#0a0f1a',
              fontSize: 13, fontWeight: 900,
              cursor: !stars ? 'not-allowed' : 'pointer',
              fontFamily: 'Tajawal,sans-serif',
            }}>
            {status === 'loading' ? t('study_reportSending')
              : !stars ? t('study_chooseRating')
              : t('study_sendReview')}
          </button>
        </div>
      )}
    </div>
  );
}

const MODE_META = {
  'كمي':    { label: 'كمي',    icon: '🔢', color: '#3b82f6', time: 70, dir: 'rtl' },
  'لفظي':   { label: 'لفظي',   icon: '📖', color: '#7c6df8', time: 60, dir: 'rtl' },
  'تحصيلي': { label: 'تحصيلي', icon: '🎓', color: '#10b981', time: 70, dir: 'rtl' },
};

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ── شبكة التنقل بين الأسئلة ──
function QuestionGrid({ questions, answers, flagged, cur, color, onNavigate, onClose }) {
  const { t, isRTL } = useLanguage();
  const answeredCount = Object.keys(answers).length;
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-page)',
          border: '1px solid #334155', borderRadius: 22,
          padding: '22px 20px', width: '100%', maxWidth: 420,
          direction: isRTL ? 'rtl' : 'ltr', fontFamily: 'Tajawal,sans-serif',
          maxHeight: '80vh', overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ color: 'var(--text)', margin: 0, fontSize: 16, fontWeight: 900 }}>
            الأسئلة ({answeredCount}/{questions.length})
          </h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--text-secondary)',
            cursor: 'pointer', fontSize: 20, padding: 0, lineHeight: 1,
          }}>✕</button>
        </div>

        {/* مفتاح الألوان */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {[[' #3b82f6', t('study_grid_current')],['#22c55e', t('study_grid_correct')],['#ef4444', t('study_grid_wrong')],['#f59e0b', t('study_grid_review')],['var(--text-muted)', t('study_grid_unanswered')]].map(([c,l]) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 12, height: 12, borderRadius: 4, background: c }} />
              <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{l}</span>
            </div>
          ))}
        </div>

        {/* الشبكة */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {questions.map((_, i) => {
            const ans = answers[i];
            const isCur = i === cur;
            const isFlagged = flagged.has(i);
            let bg = 'var(--text-muted)';
            if (isCur) bg = '#3b82f6';
            else if (isFlagged && ans === undefined) bg = '#f59e0b';
            else if (ans?.isCorrect === true) bg = '#22c55e';
            else if (ans?.isCorrect === false) bg = '#ef4444';
            return (
              <button key={i} onClick={() => onNavigate(i)} style={{
                width: 42, height: 42, borderRadius: 10, background: bg,
                border: isCur ? '2px solid #fff' : isFlagged ? '2px solid #f59e0b' : '2px solid transparent',
                color: '#fff', fontSize: 14, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
                transition: 'all .15s', position: 'relative',
              }}>
                {i + 1}
                {isFlagged && (
                  <span style={{
                    position: 'absolute', top: -4, left: -4,
                    width: 10, height: 10, borderRadius: '50%',
                    background: '#f59e0b', border: '1px solid #0f172a',
                  }} />
                )}
              </button>
            );
          })}
        </div>

        {/* إحصائيات سريعة */}
        <div style={{
          marginTop: 16, padding: '12px 14px',
          background: 'var(--bg-card-alt)', borderRadius: 12,
          display: 'flex', justifyContent: 'space-around',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#22c55e', fontWeight: 900, fontSize: 20 }}>
              {Object.values(answers).filter(a => a.isCorrect).length}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{t('study_grid_correct')}</div>
          </div>
          <div style={{ width: 1, background: 'var(--border)' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#ef4444', fontWeight: 900, fontSize: 20 }}>
              {Object.values(answers).filter(a => !a.isCorrect).length}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{t('study_grid_wrong')}</div>
          </div>
          <div style={{ width: 1, background: 'var(--border)' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#f59e0b', fontWeight: 900, fontSize: 20 }}>
              {flagged.size}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{t('study_grid_flagged')}</div>
          </div>
          <div style={{ width: 1, background: 'var(--border)' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: 'var(--text-secondary)', fontWeight: 900, fontSize: 20 }}>
              {questions.length - answeredCount}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{t('study_grid_remaining')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StudyScreen({ user, mode, subject, circleBatch, onDone, onBack }) {
  const { t } = useLanguage();
  const subjectLabel = subject?.subject && subject.subject !== 'الكل' ? ` — ${subject.subject}` : '';
  const baseMeta = MODE_META[mode] || MODE_META['كمي'];
  const meta = { ...baseMeta, label: baseMeta.label + subjectLabel };

  const [screen, setScreen]       = useState('loading');
  const [questions, setQuestions] = useState([]);
  const [cur, setCur]             = useState(0);
  const [selected, setSelected]   = useState(null);
  const [showAns, setShowAns]     = useState(false);
  const [answers, setAnswers]     = useState({});
  const [timer, setTimer]         = useState(meta.time);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showGrid, setShowGrid]   = useState(false);
  const [flagged, setFlagged]     = useState(new Set());
  const [ratings, setRatings]     = useState({}); // { [questionId]: 'up' | 'down' }
  const [initVersion, setInitVersion] = useState(0);
  const [seenCount, setSeenCount]     = useState(0); // eslint-disable-line no-unused-vars
  const [totalCount, setTotalCount]   = useState(0);
  const [hint, setHint]               = useState('');
  const [hintLoading, setHintLoading] = useState(false);
  const [aiAnalysis,  setAiAnalysis]  = useState('');
  const [aiLoading,   setAiLoading]   = useState(false);

  const sessionId    = useRef(null);
  const sessionStart = useRef(Date.now());
  const qStart       = useRef(Date.now());
  const answersRef   = useRef({});
  const questionsRef = useRef([]);

  const SAVE_KEY = `qadder_quiz_${mode}${subject?.subject ? '_' + subject.subject : ''}`;
  const SEEN_KEY = `qadder_seen_${mode}${subject?.subject ? '_' + subject.subject : ''}`;

  // ── تحميل الأسئلة ──
  useEffect(() => {
    if (initVersion === 0) {
      const saved = localStorage.getItem(SAVE_KEY);
      if (saved) {
        try {
          const p = JSON.parse(saved);
          const validAge = Date.now() - p.savedAt < 86400000;
          if (p.mode === mode && Array.isArray(p.questions) && p.questions.length && validAge) {
            questionsRef.current = p.questions;
            setQuestions(p.questions);
            setCur(p.cur ?? 0);
            // backward compat: old format had results array
            const savedAnswers = p.answers ?? (
              Array.isArray(p.results)
                ? Object.fromEntries(p.results.map((r, i) => [i, { selected: -1, isCorrect: r.correct, questionId: p.questions[i]?.id }]))
                : {}
            );
            answersRef.current = savedAnswers;
            setAnswers(savedAnswers);
            sessionId.current = p.sessionId ?? null;
            sessionStart.current = p.sessionStart ?? Date.now();
            qStart.current = Date.now();
            setTimer(meta.time);
            setScreen('resume');
            return;
          }
        } catch {}
        localStorage.removeItem(SAVE_KEY);
      }
    }

    setScreen('loading');
    (async () => {
      let qs, total;

      if (circleBatch?.ids?.length) {
        // وضع الدوائر — جلب الأسئلة بـ IDs محددة
        const { data } = await supabase
          .from('questions')
          .select('*')
          .in('id', circleBatch.ids)
          .eq('is_active', true);
        qs = data || [];
        total = qs.length;
      } else {
        const seenIds = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
        const result = await getQuestionsByMode(mode, 20, subject?.subject, seenIds);
        qs = result.data;
        total = result.total;
      }

      if (total !== undefined) setTotalCount(total);
      if (!qs?.length) { setScreen('empty'); return; }

      const shuffled = circleBatch ? qs : shuffle(qs); // الدوائر بدون خلط
      questionsRef.current = shuffled;
      setQuestions(shuffled);

      if (user?.id) {
        const testId = await getTestIdByMode(mode); // قد يكون null — لا بأس
        const { data: sess } = await createTrainingSession(user.id, testId, 'practice');
        if (sess) sessionId.current = sess.id;
      }

      sessionStart.current = Date.now();
      qStart.current = Date.now();
      setAnswers({}); answersRef.current = {};
      setTimer(meta.time);
      setScreen('quiz');
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initVersion]);

  // ── مؤقت السؤال ──
  useEffect(() => {
    if (screen !== 'quiz' || showAns) return;
    const q = questions[cur];
    let opts = [];
    try {
      if (Array.isArray(q?.options)) opts = q.options;
      else if (typeof q?.options === 'string') opts = JSON.parse(q.options);
    } catch { opts = []; }
    if (opts.length === 0) return;

    if (timer === 0) { handleSubmit(null); return; }
    const t = setTimeout(() => setTimer(x => x - 1), 1000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer, screen, showAns, cur]);

  const saveProgress = (curIdx, currentAnswers) => {
    if (!questionsRef.current.length) return;
    localStorage.setItem(SAVE_KEY, JSON.stringify({
      mode,
      sessionId: sessionId.current,
      questions:  questionsRef.current,
      cur:        curIdx,
      answers:    currentAnswers,
      sessionStart: sessionStart.current,
      savedAt:    Date.now(),
    }));
  };

  const startFresh = () => {
    localStorage.removeItem(SAVE_KEY);
    setCur(0); setSelected(null); setShowAns(false);
    setAnswers({}); answersRef.current = {};
    sessionId.current = null; questionsRef.current = [];
    setFlagged(new Set());
    setTimer(meta.time);
    setInitVersion(v => v + 1);
  };

  const navigateTo = (idx) => {
    if (idx < 0 || idx >= questions.length) return;
    setShowGrid(false);
    setHint('');
    const existing = answersRef.current[idx];
    setCur(idx);
    if (existing !== undefined) {
      setSelected(existing.selected);
      setShowAns(true);
      generateHint(questionsRef.current[idx]);
    } else {
      setSelected(null);
      setShowAns(false);
      setTimer(meta.time);
      qStart.current = Date.now();
    }
  };

  const toggleFlag = () => {
    setFlagged(prev => {
      const next = new Set(prev);
      if (next.has(cur)) next.delete(cur);
      else next.add(cur);
      return next;
    });
  };

  const generateHint = async (q) => {
    if (!q) return;
    const storedExplain = q.explanation || q.explain || '';
    if (storedExplain && !storedExplain.includes('المصدر') && !storedExplain.includes('تسريبات')) {
      setHint(storedExplain); setHintLoading(false); return;
    }
    try {
      const cacheKey = `hint_${q.id}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) { setHint(cached); setHintLoading(false); return; }

      setHint('');
      setHintLoading(true);

      const res = await fetch('/api/hint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: q.question_ar,
          options: Array.isArray(q.options) ? q.options : [],
          correctIndex: Number(q.correct_key),
          subject: q.subject || '',
        }),
      });

      const data = await res.json();
      const h = data?.hint || '';
      setHint(h);
      if (h) localStorage.setItem(cacheKey, h);
    } catch (e) {
      setHint('تعذّر توليد الشرح');
    } finally {
      setHintLoading(false);
    }
  };

  const handleSubmit = async (idx) => {
    if (answersRef.current[cur] !== undefined) return; // already answered
    const q = questions[cur];
    const chosen = idx ?? -1;
    const isCorrect = chosen === Number(q.correct_key);
    const timeSecs = Math.round((Date.now() - qStart.current) / 1000);

    const newAnswers = {
      ...answersRef.current,
      [cur]: { selected: chosen, isCorrect, questionId: q.id },
    };
    answersRef.current = newAnswers;
    setAnswers(newAnswers);
    setSelected(chosen);
    setShowAns(true);
    generateHint(q);

    saveProgress(cur, newAnswers);

    if (sessionId.current && q.id) {
      await saveAnswer(sessionId.current, q.id, chosen, isCorrect, timeSecs);
    }
  };

  const handleRate = async (questionId, rating) => {
    if (ratings[questionId]) return; // تم التقييم مسبقاً
    setRatings(prev => ({ ...prev, [questionId]: rating }));
    try {
      await supabase.from('question_reports').insert({
        question_id: questionId,
        report_type: `rating_${rating}`,
        user_phone:  user?.phone || null,
      });
      // إذا تقييم سلبي — تحقق من العدد وأخفِ إذا وصل 3
      if (rating === 'down') {
        const { count } = await supabase
          .from('question_reports')
          .select('id', { count: 'exact', head: true })
          .eq('question_id', questionId)
          .eq('report_type', 'rating_down');
        if (count >= 3) {
          await supabase.from('questions').update({
            is_active: false,
            flag_reason: 'auto_hidden_low_rating',
          }).eq('id', questionId);
        }
      }
    } catch (e) { /* silent */ }
  };

  const handleNext = () => {
    if (Object.keys(answersRef.current).length >= questions.length) {
      finishSession();
      return;
    }
    // التالي غير مجاب بعد cur
    for (let i = cur + 1; i < questions.length; i++) {
      if (answersRef.current[i] === undefined) { navigateTo(i); return; }
    }
    // من البداية
    for (let i = 0; i < cur; i++) {
      if (answersRef.current[i] === undefined) { navigateTo(i); return; }
    }
    finishSession();
  };

  // ── تحليل الأداء بـ AI ──────────────────────────────────
  const handleAIAnalysis = async () => {
    if (aiLoading || aiAnalysis) return;
    setAiLoading(true);

    // حساب الأداء حسب المادة
    const wrongBySubject   = {};
    const correctBySubject = {};
    Object.entries(answersRef.current).forEach(([idx, ans]) => {
      const q = questionsRef.current[parseInt(idx)];
      if (!q) return;
      const sub = q.subject || mode;
      if (ans.isCorrect) correctBySubject[sub] = (correctBySubject[sub] || 0) + 1;
      else               wrongBySubject[sub]   = (wrongBySubject[sub]   || 0) + 1;
    });

    const correct      = Object.values(answersRef.current).filter(a => a.isCorrect).length;
    const total        = questionsRef.current.length;
    const sessionScore = total > 0 ? Math.round((correct / total) * 100) : 0;
    const duration     = Math.round((Date.now() - sessionStart.current) / 1000);

    try {
      const res  = await fetch('/api/analyze', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode,
          totalQuestions:  total,
          correctCount:    correct,
          score:           sessionScore,
          wrongBySubject,
          correctBySubject,
          durationSecs:    duration,
        }),
      });
      const data = await res.json();
      setAiAnalysis(data.analysis || 'تعذّر توليد التحليل.');
    } catch {
      setAiAnalysis('تعذّر الاتصال بالخادم.');
    } finally {
      setAiLoading(false);
    }
  };

  const finishSession = async () => {
    localStorage.removeItem(SAVE_KEY);
    const finalAnswers = answersRef.current;
    const correct = Object.values(finalAnswers).filter(a => a.isCorrect).length;
    const wrong = Object.values(finalAnswers).filter(a => !a.isCorrect).length;
    const total = questions.length;
    const durationSecs = Math.round((Date.now() - sessionStart.current) / 1000);
    if (sessionId.current) {
      await completeSession(sessionId.current, correct, total, durationSecs);
    }
    // تحديث تقدم الدائرة
    if (circleBatch?.onUpdate) {
      circleBatch.onUpdate(total, correct, wrong, true);
    }
    setScreen('result');
  };

  const handleSaveAndExit = () => {
    saveProgress(cur, answersRef.current);
    onBack();
  };

  const timerColor = timer > 20 ? '#22c55e' : timer > 10 ? '#f59e0b' : '#ef4444';
  const answeredCount = Object.keys(answers).length;

  // ── شاشة التحميل ──
  if (screen === 'loading') return (
    <PageWrap>
      <div style={{ textAlign: 'center', color: meta.color }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{meta.icon}</div>
        <div style={{ fontSize: 16, color: 'var(--text-secondary)' }}>{t('study_loadingQuestions')}</div>
      </div>
    </PageWrap>
  );

  // ── شاشة الاستئناف ──
  if (screen === 'resume') {
    const total     = questions.length;
    const remaining = total - answeredCount;
    const pctDone   = Math.round((answeredCount / total) * 100);
    return (
      <PageWrap>
        <div style={{ textAlign: 'center', width: '100%', maxWidth: 420 }}>
          <div style={{ fontSize: 60, marginBottom: 12 }}>⏸️</div>
          <h2 style={{ color: 'var(--text)', fontSize: 22, fontWeight: 900, margin: '0 0 6px' }}>
            {t('study_savedSession')}
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 20px' }}>
            {meta.icon} تدريب {meta.label}
          </p>

          <div style={{
            background: 'var(--bg-card)', border: '1px solid #334155',
            borderRadius: 18, padding: '20px 24px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 16 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#22c55e', fontSize: 28, fontWeight: 900 }}>{answeredCount}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{t('study_answered')}</div>
              </div>
              <div style={{ width: 1, background: 'var(--border)' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#f59e0b', fontSize: 28, fontWeight: 900 }}>{remaining}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{t('study_remaining')}</div>
              </div>
              <div style={{ width: 1, background: 'var(--border)' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#7c6df8', fontSize: 28, fontWeight: 900 }}>{total}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{t('study_total')}</div>
              </div>
            </div>
            <div style={{ background: 'var(--bg-input)', borderRadius: 8, height: 8, overflow: 'hidden' }}>
              <div style={{
                background: `linear-gradient(90deg,${meta.color},#a78bfa)`,
                height: '100%', width: `${pctDone}%`, borderRadius: 8, transition: 'width 1s',
              }} />
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 6 }}>{pctDone}% {t('study_completed')}</div>
          </div>

          <button
            onClick={() => { qStart.current = Date.now(); setScreen('quiz'); }}
            style={btnStyle(`linear-gradient(135deg,${meta.color},#a78bfa)`, '#fff')}
          >{t('study_resumeBtn')}</button>
          <button
            onClick={startFresh}
            style={{ ...btnStyle('transparent', 'var(--text-secondary)'), marginTop: 10, border: '1px solid #334155' }}
          >{t('study_newSessionBtn')}</button>
        </div>
      </PageWrap>
    );
  }

  // ── لا توجد أسئلة ──
  if (screen === 'empty') return (
    <PageWrap>
      <div style={{ textAlign: 'center', maxWidth: 400 }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>📭</div>
        <h2 style={{ color: 'var(--text)', fontSize: 22, fontWeight: 900, margin: '0 0 8px' }}>
          {t('study_empty')}
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 15, margin: '0 0 24px' }}>
          {t('study_emptyDesc')}
        </p>
        <button onClick={onBack} style={btnStyle('var(--border)', 'var(--text-secondary)')}>{t('study_home')}</button>
      </div>
    </PageWrap>
  );

  // ── شاشة النتيجة ──
  if (screen === 'result') {
    const correct = Object.values(answers).filter(a => a.isCorrect).length;
    const pct = questions.length ? Math.round((correct / questions.length) * 100) : 0;
    const medal = pct >= 90 ? '🏆' : pct >= 70 ? '🥈' : pct >= 50 ? '💪' : '📚';
    const msg   = pct >= 90 ? t('study_performExcellent')
                : pct >= 70 ? t('study_performGood')
                : pct >= 50 ? t('study_performOk')
                : t('study_performBad');
    const msgColor = pct >= 90 ? '#86efac' : pct >= 70 ? '#93c5fd' : pct >= 50 ? '#fbbf24' : '#fca5a5';

    // حفظ الأسئلة المشاهدة
    const prevSeen  = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
    const newSeen   = [...new Set([...prevSeen, ...questions.map(q => q.id)])];
    localStorage.setItem(SEEN_KEY, JSON.stringify(newSeen));
    const nowSeen   = newSeen.length;
    const remaining = Math.max(0, totalCount - nowSeen);
    const sessions  = remaining > 0 ? Math.ceil(remaining / 20) : 0;

    return (
      <PageWrap>
        <div style={{ textAlign: 'center', width: '100%', maxWidth: 480 }}>
          <div style={{ fontSize: 64 }}>{medal}</div>
          <h2 style={{ color: 'var(--text)', fontSize: 26, fontWeight: 900, margin: '8px 0' }}>
            {t('study_sessionEnd')}
          </h2>

          <div style={{
            background: 'var(--bg-card)', borderRadius: 22, padding: 26,
            border: '1px solid #334155', marginBottom: 16,
          }}>
            <div style={{
              fontSize: 60, fontWeight: 900,
              background: `linear-gradient(90deg,${meta.color},#a78bfa)`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>{pct}%</div>
            <p style={{ color: 'var(--text-secondary)', fontSize: 15, margin: '4px 0 10px' }}>
              {correct} {t('study_correct')} {t('study_of')} {questions.length}
            </p>
            <div style={{ background: 'var(--bg-input)', borderRadius: 10, height: 10, overflow: 'hidden' }}>
              <div style={{
                background: pct >= 70 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444',
                height: '100%', width: `${pct}%`, borderRadius: 10, transition: 'width 1s',
              }} />
            </div>
            <p style={{ color: msgColor, fontWeight: 700, fontSize: 15, margin: '12px 0 0' }}>{msg}</p>
          </div>

          {/* إحصائية التقدم */}
          {totalCount > 0 && (
            <div style={{
              background: 'var(--bg-card)', borderRadius: 16, padding: '14px 18px',
              border: '1px solid #334155', marginBottom: 14,
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8,
              direction: 'rtl',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#22c55e', fontWeight: 900, fontSize: 20 }}>{nowSeen}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{t('study_seen')}</div>
              </div>
              <div style={{ textAlign: 'center', borderRight: '1px solid #334155', borderLeft: '1px solid #334155' }}>
                <div style={{ color: '#f59e0b', fontWeight: 900, fontSize: 20 }}>{remaining}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{t('study_remaining')}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: meta.color, fontWeight: 900, fontSize: 20 }}>{sessions}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{t('study_sessionsLeft')}</div>
              </div>
            </div>
          )}

          {/* ── تحليل AI ── */}
          <div style={{ width: '100%', marginBottom: 14 }}>
            {!aiAnalysis && !aiLoading && (
              <button onClick={handleAIAnalysis} style={{
                width: '100%',
                background: 'linear-gradient(135deg,rgba(139,92,246,0.12),rgba(124,109,248,0.08))',
                border: '1.5px solid rgba(139,92,246,0.4)',
                borderRadius: 16, padding: '16px 18px',
                cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{ fontSize: 28 }}>🤖</span>
                <div style={{ textAlign: 'right', flex: 1 }}>
                  <div style={{ color: '#a78bfa', fontWeight: 900, fontSize: 15 }}>{t('study_aiAnalyzeBtn')}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 3 }}>
                    {t('study_aiSubtitle')}
                  </div>
                </div>
                <span style={{ color: '#a78bfa', fontSize: 18 }}>✨</span>
              </button>
            )}

            {aiLoading && (
              <div style={{
                background: 'rgba(139,92,246,0.08)',
                border: '1px solid rgba(139,92,246,0.25)',
                borderRadius: 16, padding: '20px',
                textAlign: 'center',
              }}>
                <div style={{ color: '#a78bfa', fontSize: 15, fontFamily: 'Tajawal,sans-serif' }}>
                  {t('study_aiAnalyzing')}
                </div>
              </div>
            )}

            {aiAnalysis && (
              <div style={{
                background: 'rgba(139,92,246,0.06)',
                border: '1px solid rgba(139,92,246,0.3)',
                borderRadius: 16, padding: '18px 20px',
                direction: 'rtl', fontFamily: 'Tajawal,sans-serif',
              }}>
                <div style={{
                  color: '#a78bfa', fontWeight: 900, fontSize: 14,
                  marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  {t('study_aiTitle')}
                </div>
                {aiAnalysis.split('\n').filter(l => l.trim()).map((line, i) => {
                  const isHeader = line.startsWith('💪') || line.startsWith('⚠️') || line.startsWith('📌');
                  return (
                    <p key={i} style={{
                      margin: isHeader ? '12px 0 4px' : '0 0 4px',
                      color: isHeader ? 'var(--text)' : 'var(--text-secondary)',
                      fontSize: isHeader ? 14 : 13,
                      fontWeight: isHeader ? 800 : 400,
                      lineHeight: 1.7,
                    }}>
                      {line}
                    </p>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── زر اكتب رأيك ── */}
          <ReviewBox user={user} />

          {/* ── شارك نتيجتك ── */}
          <button
            onClick={() => {
              const modeLabel = mode === 'كمي' ? 'القدرات الكمي' : mode === 'لفظي' ? 'القدرات اللفظي' : mode === 'تحصيلي' ? 'التحصيلي' : 'STEP';
              const filled = Math.round(pct / 20);
              const stars = '⭐'.repeat(filled) + '▫️'.repeat(5 - filled);
              const headline = pct >= 90 ? `🏆 ${pct}% — مستوى متقدم!` : pct >= 75 ? `🔥 ${pct}% — أداء قوي جداً` : pct >= 60 ? `💪 ${pct}% — فوق المتوسط` : pct >= 40 ? `📈 ${pct}% — في الطريق الصح` : `🎯 ${pct}% — البداية من هنا`;
              const challenge = pct >= 70 ? 'هل تقدر تتجاوزني؟ 👀' : 'أتحداك تجرّب! 💬';
              const text = `${headline}\n${stars}\n\n📋 ${correct} من ${questions.length} إجابة صحيحة\n📚 ${modeLabel}\n\n${challenge}\n👇 جرّب مجاناً\nqadder.sa`;
              window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
            }}
            style={{
              ...btnStyle('linear-gradient(135deg,#25d366,#128c7e)', '#fff'),
              marginBottom: 10,
            }}
          >
            <span style={{ fontSize: 18 }}>📤</span> {t('study_shareWhatsapp')}
          </button>

          <button
            onClick={() => {
              setCur(0); setSelected(null); setShowAns(false);
              setAnswers({}); answersRef.current = {};
              setFlagged(new Set());
              setTimer(meta.time);
              sessionId.current = null;
              sessionStart.current = Date.now();
              qStart.current = Date.now();
              setAiAnalysis(''); setAiLoading(false);
              setInitVersion(v => v + 1);
            }}
            style={btnStyle(`linear-gradient(135deg,${meta.color},#a78bfa)`, '#fff')}
          >🔄 {t('study_restart')}</button>

          <button onClick={onBack} style={{ ...btnStyle('transparent', 'var(--text-secondary)'), marginTop: 10, border: '1px solid #334155' }}>
            ← {t('study_home')}
          </button>
        </div>
      </PageWrap>
    );
  }

  // ── شاشة السؤال ──
  const q = questions[cur];
  const isStep = mode === 'step';
  const isFlagged = flagged.has(cur);

  let opts = [];
  try {
    if (Array.isArray(q.options)) opts = q.options;
    else if (typeof q.options === 'string') opts = JSON.parse(q.options);
  } catch { opts = []; }
  const noOptions = opts.length === 0;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-page)',
      fontFamily: 'Tajawal,sans-serif', direction: 'rtl', // questions are always Arabic
      position: 'relative', padding: '0 0 40px',
    }}>
      {/* شبكة الأسئلة */}
      {showGrid && (
        <QuestionGrid
          questions={questions}
          answers={answers}
          flagged={flagged}
          cur={cur}
          color={meta.color}
          onNavigate={navigateTo}
          onClose={() => setShowGrid(false)}
        />
      )}

      {/* Header */}
      <div style={{
        background: 'rgba(13,17,23,0.95)',
        borderBottom: '1px solid #334155',
        padding: '10px 16px',
        paddingRight: 120, // مسافة لزر الخروج الـ fixed
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 10,
        direction: 'ltr',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <LogoIcon size={28} />
          </button>
          <div style={{
            background: `${meta.color}22`, color: meta.color,
            padding: '4px 14px', borderRadius: 50,
            fontSize: 14, fontWeight: 700, border: `1px solid ${meta.color}44`,
          }}>
            {meta.icon} {meta.label}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* زر شبكة الأسئلة */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <button onClick={() => setShowGrid(true)} style={{
              background: 'rgba(129,140,248,0.12)',
              border: '1px solid rgba(129,140,248,0.3)',
              borderRadius: 8, padding: '4px 12px', cursor: 'pointer',
              color: '#7c6df8', fontSize: 12, fontWeight: 700,
              fontFamily: 'Tajawal,sans-serif',
            }}>📋 {answeredCount}/{questions.length}</button>
            <span style={{ color: 'var(--text-muted)', fontSize: 10, whiteSpace: 'nowrap' }}>{t('study_navigate')}</span>
          </div>

          <span style={{ color: 'var(--text-secondary)', fontSize: 14, minWidth: 48, textAlign: 'center' }}>
            {cur + 1} / {questions.length}
          </span>
        </div>
      </div>

      {/* زر الخروج */}
      <button onClick={() => setShowExitDialog(true)} style={{
        position: 'fixed', top: 14, right: 16, zIndex: 9999,
        background: '#ef4444', border: 'none', color: '#ffffff',
        fontSize: 15, fontWeight: 900, cursor: 'pointer',
        borderRadius: 10, padding: '8px 18px',
        fontFamily: 'Tajawal,sans-serif',
        boxShadow: '0 4px 16px rgba(239,68,68,0.6)',
      }}>{t('study_exit')}</button>

      <div style={{ padding: '16px', maxWidth: 600, margin: '0 auto' }}>
        {/* شريط التقدم */}
        <div style={{ background: 'var(--bg-card)', borderRadius: 50, height: 6, marginBottom: 16 }}>
          <div style={{
            background: `linear-gradient(90deg,${meta.color},#a78bfa)`,
            height: '100%', borderRadius: 50,
            width: `${(answeredCount / questions.length) * 100}%`,
            transition: 'width .4s',
          }} />
        </div>

        {/* المؤقت + تمييز للمراجعة */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginBottom: 16 }}>
          <div style={{
            width: 54, height: 54, borderRadius: '50%',
            border: `4px solid ${showAns ? 'var(--text-muted)' : timerColor}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: showAns ? 'var(--text-muted)' : timerColor, fontSize: 20, fontWeight: 900,
            background: showAns ? '#4d556611' : timerColor + '11', transition: 'all .5s',
          }}>{showAns ? '✓' : timer}</div>

          <button onClick={toggleFlag} style={{
            background: isFlagged ? 'rgba(245,158,11,0.15)' : 'var(--bg-card-alt)',
            border: `1px solid ${isFlagged ? '#f59e0b' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: 10, padding: '6px 14px', cursor: 'pointer',
            color: isFlagged ? '#f59e0b' : 'var(--text-secondary)',
            fontSize: 13, fontWeight: 700, fontFamily: 'Tajawal,sans-serif',
            transition: 'all .2s',
          }}>
            {isFlagged ? t('study_flagged') : t('study_flag')}
          </button>
        </div>

        {/* السؤال */}
        <div style={{
          background: 'var(--bg-card)', borderRadius: 18, padding: '20px 22px',
          marginBottom: 14, border: `1px solid ${isFlagged ? '#f59e0b44' : 'var(--border)'}`,
          direction: isStep ? 'ltr' : 'rtl',
        }}>
          {q.sections?.name_ar && (
            <div style={{ color: meta.color, fontSize: 12, fontWeight: 700, marginBottom: 10, opacity: 0.8 }}>
              {q.sections.name_ar}
            </div>
          )}
          <p style={{
            color: 'var(--text)', fontSize: isStep ? 17 : 19,
            fontWeight: 700, margin: 0, lineHeight: 1.8,
            fontFamily: isStep ? 'Georgia,serif' : 'Tajawal,sans-serif',
          }}>{q.question_ar}</p>

          {/* صورة السؤال */}
          {q.image_url && (
            <img
              src={q.image_url}
              alt="صورة السؤال"
              style={{
                marginTop: 14, width: '100%', borderRadius: 10,
                border: '1px solid #334155', objectFit: 'contain',
                maxHeight: 280, background: '#fff',
              }}
            />
          )}
        </div>

        {/* سؤال بدون خيارات */}
        {noOptions && !showAns && (
          <div style={{
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: 14, padding: '18px 16px', marginBottom: 14, textAlign: 'center',
          }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>⚠️</div>
            <p style={{ color: '#fca5a5', fontSize: 14, margin: '0 0 14px', fontWeight: 700 }}>
              {t('study_invalidQ')}
            </p>
            <button onClick={() => handleSubmit(null)} style={{
              background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 10, padding: '10px 24px',
              color: '#fca5a5', fontSize: 14, fontWeight: 700,
              fontFamily: 'inherit', cursor: 'pointer',
            }}>{t('study_skipQ')}</button>
          </div>
        )}

        {/* الخيارات */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
          {opts.map((opt, i) => {
            let bg = 'var(--bg-card)', border = 'var(--border)', color = '#cbd5e1', fw = 500;
            if (showAns) {
              if (i === Number(q.correct_key))  { bg = '#14532d'; border = '#22c55e'; color = '#86efac'; fw = 700; }
              else if (i === selected)  { bg = '#450a0a'; border = '#ef4444'; color = '#fca5a5'; }
            } else if (selected === i) { bg = '#312e81'; border = meta.color; color = '#c7d2fe'; fw = 700; }

            return (
              <button key={i}
                onClick={() => !showAns && handleSubmit(i)}
                style={{
                  background: bg, border: `2px solid ${border}`,
                  borderRadius: 12, padding: '13px 16px',
                  color, fontSize: isStep ? 15 : 16, fontWeight: fw,
                  cursor: showAns ? 'default' : 'pointer',
                  fontFamily: isStep ? 'Georgia,serif' : 'Tajawal,sans-serif',
                  textAlign: isStep ? 'left' : 'right',
                  direction: isStep ? 'ltr' : 'rtl',
                  display: 'flex', alignItems: 'center', gap: 10,
                  transition: 'all .2s',
                }}>
                <span style={{
                  background: 'var(--bg-input)', borderRadius: 7,
                  padding: '2px 9px', fontSize: 12, color: 'var(--text-muted)',
                  minWidth: 24, textAlign: 'center',
                  fontFamily: 'Tajawal,sans-serif',
                }}>
                  {isStep ? ['A','B','C','D'][i] : ['أ','ب','ج','د'][i]}
                </span>
                <span style={{ flex: 1 }}>{opt}</span>
                {showAns && i === Number(q.correct_key) && <span>✅</span>}
                {showAns && i === selected && i !== Number(q.correct_key) && <span>❌</span>}
              </button>
            );
          })}
        </div>

        {/* نافذة الخروج */}
        {showExitDialog && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            padding: '0 0 24px',
          }}>
            <div style={{
              background: 'var(--bg-page)',
              border: '1px solid #334155',
              borderRadius: 24, padding: '28px 24px 20px',
              width: '100%', maxWidth: 440,
              direction: 'rtl', fontFamily: 'Tajawal,sans-serif',
              boxShadow: '0 -8px 40px rgba(0,0,0,0.5)',
            }}>
              <div style={{ textAlign: 'center', marginBottom: 12 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16,
                  background: 'rgba(239,68,68,0.12)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 26,
                }}>⚠️</div>
              </div>
              <h3 style={{ color: '#f1f5f9', fontSize: 18, fontWeight: 900, textAlign: 'center', margin: '0 0 6px' }}>
                {t('study_exitTitle')}
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', margin: '0 0 22px', lineHeight: 1.6 }}>
                {t('study_answered')} {answeredCount} {t('study_of')} {questions.length} {t('study_question')}
              </p>
              <button onClick={() => setShowExitDialog(false)} style={{
                width: '100%', padding: '14px',
                background: `linear-gradient(135deg,${meta.color},#a78bfa)`,
                border: 'none', borderRadius: 13,
                color: '#fff', fontSize: 15, fontWeight: 900,
                fontFamily: 'inherit', cursor: 'pointer', marginBottom: 10,
                boxShadow: `0 4px 18px ${meta.color}44`,
              }}>{t('study_continueTraining')}</button>
              <button onClick={handleSaveAndExit} style={{
                width: '100%', padding: '13px',
                background: 'var(--bg-card-alt)',
                border: '1px solid var(--border)',
                borderRadius: 13, color: 'var(--text-secondary)', fontSize: 15, fontWeight: 700,
                fontFamily: 'inherit', cursor: 'pointer', marginBottom: 8,
              }}>{t('study_saveAndExit')}</button>
              <button onClick={() => { localStorage.removeItem(SAVE_KEY); onBack(); }} style={{
                width: '100%', padding: '11px',
                background: 'none', border: 'none',
                color: '#ef4444', fontSize: 14, fontWeight: 700,
                fontFamily: 'inherit', cursor: 'pointer',
              }}>{t('study_exitNoSave')}</button>
            </div>
          </div>
        )}

        {/* الشرح + زر التالي */}
        {showAns && (
          <>
            {q.explanation && !q.explanation.includes('المصدر') && !q.explanation.includes('تسريبات') && (
              <div style={{
                background: 'var(--bg-input)', border: '1px solid #334155',
                borderRadius: 12, padding: '10px 14px', marginBottom: 12,
                direction: isStep ? 'ltr' : 'rtl',
              }}>
                <p style={{
                  color: '#fbbf24', fontSize: 13, margin: 0,
                  fontFamily: isStep ? 'Georgia,serif' : 'Tajawal,sans-serif',
                }}>💡 {q.explanation}</p>
              </div>
            )}

            {/* تنبيه مولّد بالذكاء الاصطناعي */}
            {(hintLoading || hint) && (
              <div style={{
                background: 'rgba(139,92,246,0.08)',
                border: '1px solid rgba(139,92,246,0.3)',
                borderRadius: 12, padding: '10px 14px', marginBottom: 12,
                direction: 'rtl',
              }}>
                {hintLoading ? (
                  <p style={{ color: '#a78bfa', fontSize: 13, margin: 0, fontFamily: 'Tajawal,sans-serif' }}>
                    {t('study_hintLoading')}
                  </p>
                ) : (
                  <p style={{ color: '#c4b5fd', fontSize: 13, margin: 0, fontFamily: 'Tajawal,sans-serif' }}>
                    ✨ {hint}
                  </p>
                )}
              </div>
            )}

            <AdminReplyBanner questionId={q.id} userId={user?.id} />
            <ReportButton questionId={q.id} userId={user?.id} userPhone={user?.phone} />

            <button onClick={handleNext} style={{
              width: '100%',
              background: `linear-gradient(135deg,${meta.color},#a78bfa)`,
              color: '#fff', border: 'none', borderRadius: 12,
              padding: 14, fontSize: 16, fontWeight: 900,
              cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
            }}>
              {Object.keys(answersRef.current).length >= questions.length ? t('study_viewResult') : t('study_next')}
            </button>

            {/* تقييم السؤال */}
            <QuestionRating qId={q?.id} rated={ratings[q?.id]} onRate={handleRate} />
          </>
        )}
      </div>
    </div>
  );
}

function QuestionRating({ qId, rated, onRate }) {
  const { t } = useLanguage();
  if (!qId) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', marginTop: 8 }}>
      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{t('study_questionCorrect')}</span>
      <button
        onClick={() => !rated && onRate(qId, 'up')}
        style={{
          background: rated === 'up' ? 'rgba(34,197,94,0.15)' : 'var(--bg-card)',
          border: `1px solid ${rated === 'up' ? '#22c55e' : '#334155'}`,
          borderRadius: 8, padding: '5px 14px',
          cursor: rated ? 'default' : 'pointer',
          color: rated === 'up' ? '#22c55e' : 'var(--text-secondary)',
          fontSize: 16, fontFamily: 'Tajawal,sans-serif', transition: 'all .2s',
        }}
      >👍</button>
      <button
        onClick={() => !rated && onRate(qId, 'down')}
        style={{
          background: rated === 'down' ? 'rgba(239,68,68,0.15)' : 'var(--bg-card)',
          border: `1px solid ${rated === 'down' ? '#ef4444' : '#334155'}`,
          borderRadius: 8, padding: '5px 14px',
          cursor: rated ? 'default' : 'pointer',
          color: rated === 'down' ? '#ef4444' : 'var(--text-secondary)',
          fontSize: 16, fontFamily: 'Tajawal,sans-serif', transition: 'all .2s',
        }}
      >👎</button>
      {rated && (
        <span style={{ color: rated === 'up' ? '#22c55e' : '#f87171', fontSize: 12 }}>
          {rated === 'up' ? t('study_thankUp') : t('study_reportSentTitle') + ' ✓'}
        </span>
      )}
    </div>
  );
}

function PageWrap({ children }) {
  const { isRTL } = useLanguage();
  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-page)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Tajawal,sans-serif', direction: isRTL ? 'rtl' : 'ltr', padding: 24,
    }}>
      {children}
    </div>
  );
}

function btnStyle(bg, color) {
  return {
    width: '100%', padding: '14px', borderRadius: 12,
    background: bg, color, border: 'none',
    fontSize: 16, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
  };
}
